// ════════════════════════════════════════════════════════════════════════════
//  ManDana — which device this is
//  ---------------------------------------------------------------------------
//  One fact, and the reason it needs a file of its own is where it may not be
//  kept rather than how it is made.
//
//  WHY IT EXISTS AT ALL: a device must not list ITSELF as "your other device is
//  here". Every other question presence asks — has a newcomer been told who was
//  already in the room, has a peer gone quiet for two beats — is the same
//  question, and none of it can be asked without a name to hang it on.
//
//  IT LIVES OUTSIDE `md:`, AND THAT IS THE WHOLE DESIGN. `md:` is what
//  app/md-backup.js walks, exhaustively and deliberately, so that a key added
//  next year is in the backup by construction and nobody has to remember to
//  list it. That rule is right for the user's records and wrong for this one:
//  restoring a backup onto a second device would hand it the FIRST device's
//  identity, and two devices answering to one name is a room where presence is
//  wrong and a frame cannot be attributed to anybody. `session:v1`
//  (md-sync.js:146) sits outside `md:` for the same shape of reason, and
//  `tweaks:` set the precedent before either of them.
//
//  Through md-storage like everything else. It is the only module that touches
//  localStorage, and a key that reached around it would be the exception
//  somebody has to remember.
//
//  NO ID IS A VALID ANSWER. A browser with no crypto gets '', live sync does not
//  start, and the app is exactly what it was without it — the floor the whole
//  phase stands on. Inventing a name from a clock would be worse than having
//  none: it would be a name two devices could collide on, and a collision here
//  is not a glitch, it is one device wearing another's presence.
//
//  ── AN ID BELONGS TO AN ACCOUNT, NOT TO A BROWSER ─────────────────────────
//  Two accounts used in one browser must not share an identifier that links
//  them: the id rides every frame into the account's room, so one id in two
//  rooms says "these two accounts are the same person's" to anybody watching
//  both. So the id is kept in a SLOT named after the account it was minted for.
//
//  It used to be FORGOTTEN on sign-out instead, which reads as the same sentence
//  and is not. Signing back into the same account links nothing to anything, so
//  forgetting bought no privacy there — and it cost something real. The next
//  sign-in minted an id app/md-devices.ts had never seen, enrolled it as a new
//  machine, and left the old row in the roster for ever. Marwan found it on
//  staging on 2026-08-27 with one phone in his hand and four in his list: one
//  "This device" and three that are "Not here right now" and never would be.
//  Scoping says what forgetting was trying to say, about the case that actually
//  links two accounts, and says nothing about the case that does not.
//
//  FORGETTING IS STILL THE RIGHT WORD FOR A DELETED ACCOUNT, and only for that:
//  its slot names something that no longer exists and no device will ever answer
//  to that id again. mdGateEvicted does it, beside the two marks, because it is
//  the one teardown all four ways of learning an account is gone come through.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  'use strict';

  const MD_DEVICE = 'device:v1';

  /* 64 bits. This distinguishes the handful of devices one person owns; it is
     not a secret and nothing is authorised by it — the room is named from the
     session (server/room.js), never from this. */
  const ID_BYTES = 8;

  /* A device id, and only a device id. It becomes a key in the roster document
     and arrives there from ANOTHER device, so it is checked before it is written
     down — the same rule mdTransportIsHash follows for the same reason. It also
     guards the record read below, where the other side is this browser's own
     storage and just as capable of holding something from a build nobody
     remembers. */
  const ID_RE = /^[0-9a-f]{16}$/;

  /* The slot an id sits in before anybody has signed in. Named rather than
     spelled '' at each use, because the whole of the claim rule below turns on
     which slot is this one. */
  const LOOSE = '';

  /* Bounded, like every structure this app writes from a value it did not
     invent. Nobody signs eight accounts into one browser; a shared machine that
     tries must not grow a list for ever. */
  const MAX_ACCOUNTS = 8;

  /* Asked on every frame that goes out, so the read is cached — and cached
     AGAINST ITS SLOT, because signing out and into another account changes the
     answer without reloading the page. `null` is "not resolved yet", which ''
     cannot mean: '' is a real slot and '' is also a real id. */
  let cachedSlot: string | null = null;
  let cachedId = '';

  function mint(): string {
    const c = g.crypto;
    if (!c || typeof c.getRandomValues !== 'function') return '';
    const bytes = new Uint8Array(ID_BYTES);
    c.getRandomValues(bytes);
    let out = '';
    for (const b of bytes) out += b.toString(16).padStart(2, '0');
    return out;
  }

  /** Which slot this device is answering from right now. */
  function slotNow(): string {
    const held = typeof g.mdAccountId === 'function' ? g.mdAccountId() : null;
    return typeof held === 'string' && held ? held : LOOSE;
  }

  type Slots = { seq: number; ids: Record<string, { id: string; n: number }> };

  /** The record, read defensively.

      `n` is an allocation ordinal rather than a clock, and that is deliberate:
      it is what decides which slot is evicted when the list is full, and twelve
      sign-ins inside one millisecond would give a clock nothing to sort by. This
      module has never needed to know the time and still does not. */
  function read(): Slots {
    const raw = typeof g.mdRead === 'function' ? g.mdRead(MD_DEVICE, null) : null;
    const out: Slots = { seq: 0, ids: {} };
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;

    /* The shape from before ids had accounts: one id, for whoever was using this
       browser. Reported as the loose slot, which is exactly what it was — and
       the claim rule in mdDeviceId is then what keeps it. */
    if (typeof raw.id === 'string' && ID_RE.test(raw.id)) {
      return { seq: 1, ids: { [LOOSE]: { id: raw.id, n: 1 } } };
    }

    const from = raw.ids;
    if (!from || typeof from !== 'object' || Array.isArray(from)) return out;
    for (const [slot, entry] of Object.entries(from as any)) {
      const e: any = entry;
      if (!e || typeof e !== 'object' || typeof e.id !== 'string' || !ID_RE.test(e.id)) continue;
      const n = Number.isFinite(e.n) ? Number(e.n) : 0;
      out.ids[slot] = { id: e.id, n };
      if (n > out.seq) out.seq = n;
    }
    if (Number.isFinite(raw.seq) && Number(raw.seq) > out.seq) out.seq = Number(raw.seq);
    return out;
  }

  function write(held: Slots): void {
    if (typeof g.mdWrite === 'function') {
      g.mdWrite(MD_DEVICE, { v: 2, seq: held.seq, ids: held.ids });
    }
  }

  /** Make room, oldest first, and never for the slot being answered. */
  function evict(held: Slots, keep: string): void {
    const others = Object.keys(held.ids)
      .filter((slot) => slot !== keep)
      .sort((a, b) => (held.ids[a] as { n: number }).n - (held.ids[b] as { n: number }).n);
    while (Object.keys(held.ids).length > MAX_ACCOUNTS && others.length) {
      delete held.ids[others.shift() as string];
    }
  }

  /** This device's name, minted once per account and then held. '' when the
      browser cannot generate one, which the caller must read as "no live sync"
      rather than as an error worth showing anybody. */
  function mdDeviceId(): string {
    const slot = slotNow();
    if (cachedSlot === slot) return cachedId;
    if (typeof g.mdRead !== 'function') return '';

    const held = read();
    const mine = held.ids[slot];
    if (mine) { cachedSlot = slot; cachedId = mine.id; return cachedId; }

    /* AN ID MINTED BEFORE ANYBODY SIGNED IN IS CLAIMED, not replaced. It is the
       same machine either way — but the reason it matters is the upgrade: a
       device that has been running for weeks holds the old single-id record,
       and if that record were read while signed out, minting a fresh id at
       sign-in would hand every existing device the very phantom row this
       scoping removes. Claimed once and then gone from the loose slot, so two
       accounts can never inherit one id. */
    const loose = slot !== LOOSE ? held.ids[LOOSE] : undefined;
    const minted = loose ? loose.id : mint();
    if (!minted) return '';
    if (loose) delete held.ids[LOOSE];

    held.seq += 1;
    held.ids[slot] = { id: minted, n: held.seq };
    evict(held, slot);
    /* If the write is refused the id is still returned and still used for this
       session. A device that cannot persist its name is a device whose presence
       is right until it reloads, which is better than a device with no name. */
    write(held);
    cachedSlot = slot;
    cachedId = minted;
    return cachedId;
  }

  function mdDeviceIsId(value: unknown): boolean {
    return typeof value === 'string' && ID_RE.test(value);
  }

  /** Forget the id of the account being held — both copies, the cached one and
      the stored one, because forgetting only the store would leave this tab
      answering to the old name until it reloaded.

      One account's slot, never the list. This is what a DELETED account wants;
      an ordinary sign-out wants nothing at all, and mdResetSyncState no longer
      asks (see the header). */
  function mdDeviceForget(): void {
    const slot = slotNow();
    cachedSlot = null;
    cachedId = '';
    if (typeof g.mdRead !== 'function') return;

    const held = read();
    if (!(slot in held.ids)) return;
    delete held.ids[slot];
    if (!Object.keys(held.ids).length) {
      if (typeof g.mdRemove === 'function') g.mdRemove(MD_DEVICE);
      return;
    }
    write(held);
  }

  Object.assign(g, { mdDeviceId, mdDeviceIsId, mdDeviceForget });
})(typeof window !== 'undefined' ? window : globalThis);
